// Notes thread inside the order detail modal (admin Orders page).
//
// Replaces the single overwritable `orders.notes` textarea. Each note is
// saved the moment "Add note" is pressed — it does NOT ride the modal's Save
// button — and is stamped server-side with the session user and the DB
// clock. Any order can take a note, whatever its status.
//
// Edit / Delete appear only on notes the BE marks `editable`: the caller's
// own notes, inside an hour of adding them. The BE decides that on its
// clock; the FE just re-checks `editable_until` on a slow tick so the
// controls disappear when the hour runs out while the modal is open.
import { useEffect, useState } from "react";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { apiFetch } from "../../api/apiFetch";
import { useReadOnly } from "../../access/ReadOnly";

export interface OrderNote {
    note_id: string;
    order_id: string;
    body: string;
    created_by_uid: number | null;
    created_by_name: string | null;
    created_at: string;
    updated_at: string;
    edited: boolean;
    editable: boolean;
    editable_until: string | null;
}

export const NOTE_MAX_LEN = 4000;

const formatTs = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
};

const isEditableNow = (n: OrderNote, now: number): boolean => {
    if (!n.editable) return false;
    if (!n.editable_until) return true;
    const until = Date.parse(n.editable_until);
    return Number.isNaN(until) ? true : until > now;
};

const OrderNotes = ({
    orderId,
    t,
    onLatestChange,
}: {
    orderId: string;
    t: (k: string, p?: Record<string, string | number>) => string;
    // Fires with the newest note's text (null when the thread is empty) so
    // the page can keep the Orders table "Notes" column in step.
    onLatestChange?: (latest: string | null) => void;
}) => {
    const readOnly = useReadOnly();
    const [notes, setNotes] = useState<OrderNote[]>([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState<string | null>(null); // "add" | note_id
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState("");
    const [now, setNow] = useState(() => Date.now());
    // The note whose removal is awaiting confirmation — a compact in-app
    // dialog rather than the browser's confirm(), which styles itself.
    const [pendingDelete, setPendingDelete] = useState<OrderNote | null>(null);

    const load = async (alive?: () => boolean) => {
        try {
            const r = await apiFetch(`/orders/${encodeURIComponent(orderId)}/notes`, {});
            if (!r.ok || (alive && !alive())) return;
            const data: { notes: OrderNote[] } = await r.json();
            if (alive && !alive()) return;
            setNotes(Array.isArray(data.notes) ? data.notes : []);
            setNow(Date.now());
        } catch {
            if (!alive || alive()) toast.error(t("server_error"), { toastId: "notes-load-err" });
        }
    };

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setDraft("");
        setEditingId(null);
        load(() => mounted).finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    // Only tick while something could still expire.
    const anyDeadline = notes.some(n => n.editable && n.editable_until);
    useEffect(() => {
        if (!anyDeadline) return;
        const id = window.setInterval(() => setNow(Date.now()), 30_000);
        return () => window.clearInterval(id);
    }, [anyDeadline]);

    const failWith = async (r: Response, fallbackToastId: string) => {
        const body = await r.json().catch(() => ({}));
        toast.error(body?.message || t("server_error"), { toastId: fallbackToastId });
        // A 403/409 means our `editable` flags are stale — refresh them.
        if (r.status === 403 || r.status === 409 || r.status === 404) await load();
    };

    const onAdd = async () => {
        if (readOnly) return;
        const body = draft.trim();
        if (!body || busy !== null) return;
        setBusy("add");
        try {
            const r = await apiFetch(`/orders/${encodeURIComponent(orderId)}/notes`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ body }),
            });
            if (!r.ok) { await failWith(r, "notes-add-err"); return; }
            const data: { note: OrderNote; latest_note: string | null } = await r.json();
            setNotes(prev => [data.note, ...prev]);
            setNow(Date.now());
            setDraft("");
            onLatestChange?.(data.latest_note ?? null);
            toast.success(t("order_notes_added"), { toastId: "notes-added" });
        } catch {
            toast.error(t("server_error"), { toastId: "notes-add-err" });
        } finally {
            setBusy(null);
        }
    };

    const onSaveEdit = async (n: OrderNote) => {
        if (readOnly) return;
        const body = editDraft.trim();
        if (!body || busy !== null) return;
        setBusy(n.note_id);
        try {
            const r = await apiFetch(
                `/orders/${encodeURIComponent(orderId)}/notes/${encodeURIComponent(n.note_id)}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ body }),
                },
            );
            if (!r.ok) { await failWith(r, "notes-edit-err"); return; }
            const data: { note: OrderNote; latest_note: string | null } = await r.json();
            setNotes(prev => prev.map(x => (x.note_id === n.note_id ? data.note : x)));
            setEditingId(null);
            onLatestChange?.(data.latest_note ?? null);
        } catch {
            toast.error(t("server_error"), { toastId: "notes-edit-err" });
        } finally {
            setBusy(null);
        }
    };

    const onDelete = async (n: OrderNote) => {
        if (readOnly || busy !== null) return;
        setPendingDelete(null);
        setBusy(n.note_id);
        try {
            const r = await apiFetch(
                `/orders/${encodeURIComponent(orderId)}/notes/${encodeURIComponent(n.note_id)}`,
                { method: "DELETE" },
            );
            if (!r.ok) { await failWith(r, "notes-del-err"); return; }
            const data: { deleted: boolean; latest_note: string | null } = await r.json();
            setNotes(prev => prev.filter(x => x.note_id !== n.note_id));
            if (editingId === n.note_id) setEditingId(null);
            onLatestChange?.(data.latest_note ?? null);
            toast.success(t("order_notes_deleted"), { toastId: "notes-deleted" });
        } catch {
            toast.error(t("server_error"), { toastId: "notes-del-err" });
        } finally {
            setBusy(null);
        }
    };

    const submitOnCtrlEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>, action: () => void) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            action();
        }
    };

    return (
        <div className="order-notes">
            <label className="dispatch-edit-label" htmlFor={`order-notes-draft-${orderId}`}>{t("note")}</label>
            <textarea
                id={`order-notes-draft-${orderId}`}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => submitOnCtrlEnter(e, () => void onAdd())}
                placeholder={t("note_placeholder")}
                rows={2}
                maxLength={NOTE_MAX_LEN}
                className="dispatch-note-input"
                disabled={busy === "add" || readOnly}
            />
            <div className="order-notes-add-row">
                <span className="order-notes-hint">{t("order_notes_hint")}</span>
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={!draft.trim() || busy !== null || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                    onClick={() => void onAdd()}
                >
                    {busy === "add" && <Loader2 size={14} className="spin" />}
                    {t("add_note")}
                </button>
            </div>

            {loading ? (
                <div className="order-notes-empty">{t("loading")}</div>
            ) : notes.length === 0 ? (
                <div className="order-notes-empty">{t("order_notes_empty")}</div>
            ) : (
                <ul className="order-notes-list">
                    {notes.map(n => {
                        const canEdit = isEditableNow(n, now);
                        const isEditing = editingId === n.note_id;
                        return (
                            <li key={n.note_id} className="order-notes-item">
                                <div className="order-notes-meta">
                                    <span className="order-notes-author">
                                        {n.created_by_name || t("order_notes_unknown_author")}
                                    </span>
                                    <span className="order-notes-time">{formatTs(n.created_at)}</span>
                                    {n.edited && (
                                        <span className="order-notes-edited">{t("order_notes_edited")}</span>
                                    )}
                                    {canEdit && !isEditing && (
                                        <span className="order-notes-actions">
                                            <button
                                                type="button"
                                                className="order-notes-icon-btn"
                                                aria-label={t("edit")}
                                                title={readOnly ? t("read_only_mode") : t("edit")}
                                                disabled={busy !== null || readOnly}
                                                aria-disabled={readOnly}
                                                onClick={() => { setEditingId(n.note_id); setEditDraft(n.body); }}
                                            >
                                                <Pencil size={13} />
                                            </button>
                                            <button
                                                type="button"
                                                className="order-notes-icon-btn order-notes-icon-btn--danger"
                                                aria-label={t("delete")}
                                                title={readOnly ? t("read_only_mode") : t("delete")}
                                                disabled={busy !== null || readOnly}
                                                aria-disabled={readOnly}
                                                onClick={() => setPendingDelete(n)}
                                            >
                                                {busy === n.note_id
                                                    ? <Loader2 size={13} className="spin" />
                                                    : <Trash2 size={13} />}
                                            </button>
                                        </span>
                                    )}
                                </div>
                                {isEditing ? (
                                    <div className="order-notes-edit">
                                        <textarea
                                            value={editDraft}
                                            onChange={e => setEditDraft(e.target.value)}
                                            onKeyDown={e => submitOnCtrlEnter(e, () => void onSaveEdit(n))}
                                            rows={2}
                                            maxLength={NOTE_MAX_LEN}
                                            className="dispatch-note-input order-notes-edit-input"
                                            disabled={busy !== null}
                                            autoFocus
                                        />
                                        <div className="order-notes-edit-actions">
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm"
                                                disabled={busy !== null}
                                                onClick={() => setEditingId(null)}
                                            >
                                                {t("cancel")}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                disabled={!editDraft.trim() || busy !== null || readOnly}
                                                aria-disabled={readOnly}
                                                title={readOnly ? t("read_only_mode") : undefined}
                                                onClick={() => void onSaveEdit(n)}
                                            >
                                                {busy === n.note_id && <Loader2 size={14} className="spin" />}
                                                {t("save")}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="order-notes-body">{n.body}</div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {pendingDelete && (
                <div
                    className="modal-overlay order-notes-confirm-overlay"
                    onClick={() => setPendingDelete(null)}
                    onKeyDown={e => { if (e.key === "Escape") setPendingDelete(null); }}
                >
                    <div
                        className="modal-card order-notes-confirm"
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby={`order-notes-confirm-title-${orderId}`}
                        onClick={e => e.stopPropagation()}
                    >
                        <p id={`order-notes-confirm-title-${orderId}`} className="order-notes-confirm-title">
                            {t("order_notes_delete_confirm")}
                        </p>
                        <p className="order-notes-confirm-body">{pendingDelete.body}</p>
                        <div className="order-notes-confirm-actions">
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setPendingDelete(null)}
                            >
                                {t("cancel")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                autoFocus
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                                onClick={() => void onDelete(pendingDelete)}
                            >
                                <Trash2 size={13} />
                                {t("order_notes_delete_action")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderNotes;
